/**
 * GoogleLoginButton Component
 *
 * SCRUM-1788: Custom button overlay to prevent GIS PII exposure.
 *
 * Google Identity Services (GIS) renderButton() personalizes the button
 * with the user's name and email when an active Google session exists.
 * There is NO renderButton() configuration to disable this — `type`,
 * `auto_select`, and `disableAutoSelect()` do not control button
 * personalization (confirmed by reading @react-oauth/google source and
 * GIS API surface). See .scratch/SCRUM-1788_INVESTIGATION.txt.
 *
 * Fix: Render the GIS button invisibly (opacity: 0) on top of a custom
 * "Sign in with Google" button. The invisible GIS iframe captures clicks
 * and handles the OAuth popup flow, returning an ID token via the
 * initialize() callback. The visible custom button shows only generic
 * text — no PII is possible since it's our HTML, not Google's iframe.
 *
 * @version 2.0
 * @created 2025-11-19
 * @modified 2026-04-24
 */

import React, { useRef, useEffect } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';

/**
 * Google G logo SVG — matches Google's branding guidelines.
 * Used in the custom visible button.
 */
function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

/**
 * Google Login Button Component
 *
 * Renders a generic "Sign in with Google" button that never exposes PII,
 * while preserving the GIS OAuth flow for ID token retrieval.
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Called with credentialResponse on successful login
 * @param {Function} props.onError - Called on login error
 * @param {boolean} props.disabled - Disable the button
 */
export default function GoogleLoginButton({ onSuccess, onError, disabled = false }) {
  const btnContainerRef = useRef(null);
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!scriptLoadedSuccessfully) {
      return;
    }

    const gis = window.google?.accounts?.id;
    if (!gis) {
      return;
    }

    // Prevent automatic sign-in prompts (One Tap / FedCM)
    gis.disableAutoSelect();

    // Initialize GIS with credential callback (returns ID token JWT)
    gis.initialize({
      client_id: clientId,
      callback: (credentialResponse) => {
        if (!credentialResponse?.credential) {
          onErrorRef.current?.();
          return;
        }
        const { credential, select_by } = credentialResponse;
        onSuccessRef.current({
          credential,
          clientId: credentialResponse.clientId || credentialResponse.client_id,
          select_by,
        });
      },
      auto_select: false,
    });

    // Render GIS button invisibly — the iframe handles click → OAuth popup
    if (btnContainerRef.current) {
      gis.renderButton(btnContainerRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: 300,
      });
    }
  }, [clientId, scriptLoadedSuccessfully]);

  return (
    <div
      className="w-full flex justify-center"
      data-testid="google-login-wrapper"
    >
      <div
        style={{
          position: 'relative',
          width: '300px',
          height: '40px',
        }}
      >
        {/* GIS button: invisible but on top to receive clicks via iframe */}
        <div
          ref={btnContainerRef}
          data-testid="gis-button-container"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            zIndex: 2,
            cursor: disabled ? 'not-allowed' : 'pointer',
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        />
        {/* Custom visible button: generic text, no PII possible */}
        <div
          data-testid="google-login-btn"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            border: '1px solid #dadce0',
            borderRadius: '4px',
            backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
            fontSize: '14px',
            fontFamily: "'Google Sans', Roboto, Arial, sans-serif",
            fontWeight: 500,
            color: disabled ? '#9e9e9e' : '#3c4043',
            letterSpacing: '0.25px',
          }}
        >
          <GoogleLogo />
          Sign in with Google
        </div>
      </div>
    </div>
  );
}
